"use client";
import React, { useCallback, useRef } from "react";
import { Drawer } from "vaul";
import Icon from "@/components/icons";
import {
  CURRENCY_SELECTOR_OVERLAY_Z_CLASS,
  DEFAULT_CURRENCY_SELECTOR_MODAL_CURRENCIES,
} from "../constants";
import { cn } from "@/lib/utils";
import useTranslations from "@/i18n/useTranslations";

interface CurrencyBottomSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedCurrency: string;
  onSelect: (code: string) => void;
}

export default function CurrencyBottomSheet({
  open,
  onOpenChange,
  selectedCurrency,
  onSelect,
}: CurrencyBottomSheetProps) {
  const groupRef = useRef<HTMLDivElement>(null);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    const items =
      groupRef.current?.querySelectorAll<HTMLElement>('[role="radio"]');
    if (!items?.length) return;

    const currentIndex = Array.from(items).findIndex(
      (el) => el === document.activeElement,
    );
    let nextIndex: number | null = null;

    if (e.key === "ArrowDown" || e.key === "ArrowRight") {
      e.preventDefault();
      nextIndex = (currentIndex + 1) % items.length;
    } else if (e.key === "ArrowUp" || e.key === "ArrowLeft") {
      e.preventDefault();
      nextIndex = (currentIndex - 1 + items.length) % items.length;
    } else if (e.key === "Home") {
      e.preventDefault();
      nextIndex = 0;
    } else if (e.key === "End") {
      e.preventDefault();
      nextIndex = items.length - 1;
    }

    if (nextIndex != null) {
      items[nextIndex].focus();
    }
  }, []);

  const tDialog = useTranslations("Dialog");
  const tCurrency = useTranslations("Currency");
  const selectedIndex = DEFAULT_CURRENCY_SELECTOR_MODAL_CURRENCIES.findIndex(
    (c) => c.code === selectedCurrency,
  );

  return (
    <Drawer.Root open={open} onOpenChange={onOpenChange}>
      <Drawer.Portal>
        <Drawer.Overlay
          className={cn(
            "fixed inset-0 bg-black/40",
            CURRENCY_SELECTOR_OVERLAY_Z_CLASS,
          )}
        />
        <Drawer.Content
          className={cn(
            "fixed inset-x-0 bottom-0 flex max-h-[min(85vh,calc(100dvh-1rem))] flex-col rounded-t bg-white pb-[env(safe-area-inset-bottom)]",
            CURRENCY_SELECTOR_OVERLAY_Z_CLASS,
          )}
          aria-label={tCurrency("chooseLocalCurrency")}
        >
          <div className="flex items-center justify-between border-b px-4 py-2.5">
            <Drawer.Title className="m-0 text-md font-semibold">
              {tCurrency("chooseLocalCurrency")}
            </Drawer.Title>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              aria-label={tDialog("close")}
              className="flex h-10 w-10 items-center justify-center rounded-full border-2 border-gray-300 transition-colors hover:bg-putty-100"
            >
              <Icon type="close" svgProps={{ width: 24, height: 24 }} />
            </button>
          </div>
          <div
            ref={groupRef}
            role="radiogroup"
            aria-label={tCurrency("optionsAriaLabel")}
            className="overflow-y-auto"
            onKeyDown={handleKeyDown}
          >
            {DEFAULT_CURRENCY_SELECTOR_MODAL_CURRENCIES.map(
              (currency, index) => {
                const isSelected = currency.code === selectedCurrency;

                return (
                  <button
                    key={currency.code}
                    type="button"
                    role="radio"
                    aria-checked={isSelected}
                    tabIndex={
                      isSelected || (selectedIndex === -1 && index === 0)
                        ? 0
                        : -1
                    }
                    onClick={() => onSelect(currency.code)}
                    className={cn(
                      "flex min-h-11 w-full items-center gap-3 px-3 py-3 text-sm transition-colors hover:bg-putty-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500",
                      isSelected && "bg-putty-100 font-medium",
                    )}
                  >
                    <span aria-hidden="true">{currency.flag}</span>
                    <span className="font-semibold">
                      {currency.symbol}
                      {currency.code}
                    </span>
                    <span>- {currency.name}</span>
                  </button>
                );
              },
            )}
          </div>
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  );
}
